/**
 * Enhanced Testimonial Carousel Component
 * For use with Shopify Dawn Theme
 */
class TestimonialCarousel extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    // Initialize only when the DOM is fully loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupCarousel());
    } else {
      this.setupCarousel();
    }
  }
  
  setupCarousel() {
    // Get all carousel elements
    this.carousel = this.querySelector('.fatcat-testimonial-carousel');
    this.slides = this.querySelectorAll('.fatcat-testimonial-slide');
    this.dots = this.querySelectorAll('.carousel-dot');
    
    // Set up initial state
    this.currentSlide = 0;
    this.interval = null;
    this.autoplaySpeed = 6000; // 6 seconds between slides for better readability
    this.isPlaying = true;
    
    // If no slides are found, exit early
    if (this.slides.length === 0) return;
    
    // Set up event listeners for dots
    this.dots.forEach(dot => {
      dot.addEventListener('click', this.handleDotClick.bind(this));
    });
    
    // Add touch and swipe support
    this.setupSwipeSupport();
    
    // Add event listeners for focus/blur to pause/resume
    this.addEventListener('mouseenter', this.pauseAutoSlide.bind(this));
    this.addEventListener('mouseleave', this.resumeAutoSlide.bind(this));
    this.addEventListener('focusin', this.pauseAutoSlide.bind(this));
    this.addEventListener('focusout', this.resumeAutoSlide.bind(this));
    
    // Add aria-live for accessibility
    this.carousel.setAttribute('aria-live', 'polite');
    
    // Start the automatic slideshow
    this.startAutoSlide();
  }
  
  // Handle when a dot is clicked
  handleDotClick(event) {
    const dot = event.currentTarget;
    if (!dot.hasAttribute('data-slide')) return;
    
    const slideIndex = parseInt(dot.getAttribute('data-slide'));
    this.goToSlide(slideIndex);
    
    // Reset interval timer when manually changing slides
    this.pauseAutoSlide();
    this.resumeAutoSlide();
  }
  
  // Setup swipe support for mobile devices
  setupSwipeSupport() {
    let touchStartX = 0;
    let touchEndX = 0;
    
    this.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });
    
    this.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      this.handleSwipe(touchStartX, touchEndX);
    }, { passive: true });
  }
  
  // Handle swipe gestures
  handleSwipe(startX, endX) {
    const swipeThreshold = 50;
    
    if (startX - endX > swipeThreshold) {
      // Swipe left - go to next slide
      this.nextSlide();
      this.pauseAutoSlide();
      this.resumeAutoSlide();
    } else if (endX - startX > swipeThreshold) {
      // Swipe right - go to previous slide
      this.prevSlide();
      this.pauseAutoSlide();
      this.resumeAutoSlide();
    }
  }
  
  // Go to a specific slide
  goToSlide(index) {
    if (index < 0 || index >= this.slides.length) return;
    
    // Remove active class from all slides and dots
    this.slides.forEach(slide => slide.classList.remove('active'));
    this.dots.forEach(dot => dot.classList.remove('active'));
    
    // Add active class to current slide and dot
    this.slides[index].classList.add('active');
    this.dots[index].classList.add('active');
    
    // Update current slide index
    this.currentSlide = index;
    
    // Announce slide change for screen readers
    this.announceSlideChange(index);
  }
  
  // Go to next slide
  nextSlide() {
    let nextIndex = this.currentSlide + 1;
    if (nextIndex >= this.slides.length) {
      nextIndex = 0;
    }
    this.goToSlide(nextIndex);
  }
  
  // Go to previous slide
  prevSlide() {
    let prevIndex = this.currentSlide - 1;
    if (prevIndex < 0) {
      prevIndex = this.slides.length - 1;
    }
    this.goToSlide(prevIndex);
  }
  
  // Announce slide change for accessibility
  announceSlideChange(index) {
    const slide = this.slides[index];
    if (!slide) return;
    
    const name = slide.querySelector('.testimonial-name')?.textContent || '';
    const role = slide.querySelector('.testimonial-role')?.textContent || '';
    
    const liveRegion = this.querySelector('.sr-only') || this.createLiveRegion();
    liveRegion.textContent = `Showing testimonial from ${name}, ${role}`;
  }
  
  // Create live region for screen reader announcements
  createLiveRegion() {
    const liveRegion = document.createElement('div');
    liveRegion.className = 'sr-only';
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.style.position = 'absolute';
    liveRegion.style.width = '1px';
    liveRegion.style.height = '1px';
    liveRegion.style.padding = '0';
    liveRegion.style.margin = '-1px';
    liveRegion.style.overflow = 'hidden';
    liveRegion.style.clip = 'rect(0, 0, 0, 0)';
    liveRegion.style.whiteSpace = 'nowrap';
    liveRegion.style.border = '0';
    this.appendChild(liveRegion);
    return liveRegion;
  }
  
  // Start automatic slideshow
  startAutoSlide() {
    if (this.interval) {
      clearInterval(this.interval);
    }
    
    this.isPlaying = true;
    this.interval = setInterval(() => {
      this.nextSlide();
    }, this.autoplaySpeed);
  }
  
  // Pause automatic slideshow
  pauseAutoSlide() {
    clearInterval(this.interval);
    this.interval = null;
    this.isPlaying = false;
  }
  
  // Resume automatic slideshow if it was playing before
  resumeAutoSlide() {
    if (!this.isPlaying) return;
    this.startAutoSlide();
  }
  
  // Clean up when the element is removed from the DOM
  disconnectedCallback() {
    this.pauseAutoSlide();
    
    // Remove event listeners
    if (this.dots) {
      this.dots.forEach(dot => {
        dot.removeEventListener('click', this.handleDotClick);
      });
    }
    
    this.removeEventListener('mouseenter', this.pauseAutoSlide);
    this.removeEventListener('mouseleave', this.resumeAutoSlide);
    this.removeEventListener('focusin', this.pauseAutoSlide);
    this.removeEventListener('focusout', this.resumeAutoSlide);
  }
}

// Register the custom element
customElements.define('testimonial-carousel', TestimonialCarousel);